import React, { useState, useEffect, useCallback, useRef } from 'react';
// import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Snackbar,
  Chip,
  Collapse,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PendingIcon from '@mui/icons-material/Pending';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import SortIcon from '@mui/icons-material/Sort';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

const CATEGORY_COLORS = {
  SALUDO: { bg: '#10b981', label: 'Saludo', desc: 'Saludos iniciales y primer contacto' },
  TOOL_USE: { bg: '#0ea5e9', label: 'Tool Use', desc: 'Gestión de reservaciones, disponibilidad y booking' },
  MEMORIA: { bg: '#8b5cf6', label: 'Memoria', desc: 'Preguntas sobre conversaciones anteriores' },
  INFORMACION: { bg: '#f59e0b', label: 'Información', desc: 'Consultas sobre servicios, precios o ubicación' },
  CONVERSACION: { bg: '#64748b', label: 'Conversación', desc: 'Charla general, agradecimientos, despedidas' },
  ESCALACION: { bg: '#dc2626', label: 'Escalación', desc: 'Solicitud de hablar con humano o emergencia' },
  DESCONOCIDA: { bg: '#94a3b8', label: 'Desconocida', desc: 'No se pudo determinar la categoría' },
};

const getCatColor = (cat) => CATEGORY_COLORS[cat] || { bg: '#94a3b8', label: cat };

// Los once escenarios que simula la pestaña "Pipeline".
//
// Venían heredados de NARA Care —síntomas, alergias, resultados de
// laboratorio, notificar al doctor— y describían un flujo que en esta
// instalación no existe. Están reescritos contra el pipeline real de turismo:
// las seis categorías que devuelve `central_classifier.py` (SALUDO, TOOL_USE,
// MEMORIA, INFORMACION, CONVERSACION, ESCALACION — SINTOMAS se eliminó) y los
// tools que de verdad están registrados en `agent_tools.py`.
const PIPELINE_SCENARIOS = [
  {
    message: 'Hola, buenos días',
    category: 'SALUDO',
    chipLabel: 'Saludo',
    entrada: 'Texto detectado',
    gates: 'Sesión creada, privacidad OK',
    memoria: 'Sin memorias previas',
    memoriaCount: 0,
    clasificador: 'SALUDO',
    confianza: 95,
    handler: 'Saludo de agente de viajes, con el nombre del turista',
    post: 'Historial de sesión guardado',
    response: '¡Hola María! 🌴 Soy el asistente de Entretur. ¿Te antoja un pasadía a las Islas del Rosario?',
  },
  {
    message: '¿Qué salidas hay para mañana?',
    category: 'TOOL_USE',
    chipLabel: 'Disponibilidad',
    entrada: 'Texto detectado',
    gates: 'Sesión activa encontrada',
    memoria: '1 memoria: prefiere Playa Blanca',
    memoriaCount: 1,
    clasificador: 'TOOL_USE',
    confianza: 97,
    handler: 'GPT-4o ejecuta consultar_salidas_disponibles → cupos y precios',
    post: 'Tokens registrados: 1,240 (GPT-4o)',
    response: '🚤 Para mañana tenemos:\n\n• Islas del Rosario · 8:00 AM · 12 cupos · $200.000\n• Playa Blanca · 8:30 AM · 5 cupos · $180.000\n\n¿Cuál te sirve?',
  },
  {
    message: 'Quiero reservar 4 personas para el sábado',
    category: 'TOOL_USE',
    chipLabel: 'Reserva',
    entrada: 'Texto detectado',
    gates: 'Sesión activa encontrada',
    memoria: 'Sin memorias relevantes',
    memoriaCount: 0,
    clasificador: 'TOOL_USE',
    confianza: 98,
    handler: 'crear_reservacion con bloqueo de cupos (SELECT FOR UPDATE)',
    post: 'Reserva creada + comprobante por WhatsApp',
    response: '✅ Listo. Reserva ENT-20260905-018 para 4 personas, sábado 8:00 AM en el Muelle de la Bodeguita.\n\nTotal: $800.000',
  },
  {
    message: '¿Qué me recomendaste la vez pasada?',
    category: 'MEMORIA',
    chipLabel: 'Memoria',
    entrada: 'Texto detectado',
    gates: 'Sesión activa encontrada',
    memoria: '1 memoria: prefirió Barú por el trayecto corto (sim: 0.82)',
    memoriaCount: 1,
    clasificador: 'MEMORIA',
    confianza: 92,
    handler: 'Busca memorias (umbral 0.35) → respuesta contextual',
    post: 'Sin almacenamiento adicional',
    response: '🤗 La vez pasada te recomendé Playa Blanca en Barú, porque me dijiste que el trayecto largo te mareaba.',
  },
  {
    message: '¿Cuánto cuesta el pasadía?',
    category: 'INFORMACION',
    chipLabel: 'Información',
    entrada: 'Texto detectado',
    gates: 'Sesión activa encontrada',
    memoria: 'Sin memorias relevantes',
    memoriaCount: 0,
    clasificador: 'INFORMACION',
    confianza: 96,
    handler: 'Busca en la base de conocimiento → precios e inclusiones',
    post: 'Conversación abierta',
    response: '💰 El pasadía a Islas del Rosario cuesta $200.000 por adulto e incluye transporte en lancha y almuerzo. No incluye el impuesto de zarpe.',
  },
  {
    message: 'Ok, muchas gracias',
    category: 'CONVERSACION',
    chipLabel: 'Cierre',
    entrada: 'Texto detectado',
    gates: 'Sesión activa encontrada',
    memoria: 'Sin memorias relevantes',
    memoriaCount: 0,
    clasificador: 'CONVERSACION',
    confianza: 89,
    handler: 'Respuesta natural con la personalidad del chatbot',
    post: 'Cierre temporal · conversación contabilizada',
    response: '¡Con mucho gusto! 🌊 Cualquier cosa me escribes. ¡Buen viaje!',
  },
  {
    message: 'Necesito hablar con alguien real',
    category: 'ESCALACION',
    chipLabel: 'Escalación',
    entrada: 'Texto detectado',
    gates: 'Sesión activa encontrada',
    memoria: 'Sin memorias relevantes',
    memoriaCount: 0,
    clasificador: 'ESCALACION',
    confianza: 94,
    handler: 'transferir_a_humano → avisa al coordinador',
    post: 'Escalación pendiente registrada',
    response: '👋 Claro, ya le avisé al equipo coordinador. En un momento te escriben por aquí.',
  },
  {
    message: '📷 [Foto del comprobante de pago]',
    category: 'TOOL_USE',
    chipLabel: 'Comprobante',
    entrada: 'Imagen detectada',
    gates: 'Sesión activa encontrada',
    memoria: '1 memoria: reserva ENT-20260905-018 pendiente de pago',
    memoriaCount: 1,
    clasificador: 'TOOL_USE',
    confianza: 96,
    handler: 'Claude Vision lee el comprobante → monto y referencia',
    post: 'Voucher asociado a la reserva',
    response: '📸 Recibí tu comprobante por $400.000. Lo dejo registrado como abono de la reserva ENT-20260905-018. Saldo: $400.000.',
  },
  {
    message: 'Mándame fotos del Cogela Suave',
    category: 'TOOL_USE',
    chipLabel: 'Fotos',
    entrada: 'Texto detectado',
    gates: 'Sesión activa encontrada',
    memoria: 'Sin memorias relevantes',
    memoriaCount: 0,
    clasificador: 'TOOL_USE',
    confianza: 95,
    handler: 'enviar_imagenes_embarcacion — tolera escritura coloquial',
    post: 'Tokens registrados · 3 imágenes enviadas',
    response: '🚤 Te mando fotos del Cogela Suave. Capacidad 20 pax, con techo y equipo de sonido.',
  },
  {
    message: 'Cuánto cupo tengo disponible?',
    category: 'TOOL_USE',
    chipLabel: 'Agencia',
    entrada: 'Texto detectado',
    gates: 'Número reconocido como contacto de agencia',
    memoria: 'Rama agencia — sin memoria de turista',
    memoriaCount: 0,
    clasificador: 'TOOL_USE',
    confianza: 97,
    handler: 'consultar_cupo_agencia → cupo de crédito y saldo',
    post: 'Tokens registrados (rama agencia)',
    response: 'Cupo asignado: $5.000.000\nUtilizado: $3.200.000\nDisponible: $1.800.000',
  },
  {
    message: '¿Dónde queda el muelle?',
    category: 'INFORMACION',
    chipLabel: 'Logística',
    entrada: 'Texto detectado',
    gates: 'Sesión activa encontrada',
    memoria: 'Sin memorias relevantes',
    memoriaCount: 0,
    clasificador: 'INFORMACION',
    confianza: 97,
    handler: 'Base de conocimiento → punto de embarque e instrucciones',
    post: 'Conversación abierta',
    response: '📍 El embarque es en el Muelle de la Bodeguita, Centro Histórico. Te esperamos 30 minutos antes de la hora de zarpe.',
  },
];

// El log paso a paso de cada escenario. Las funciones y los tools que se
// nombran aquí existen: `central_classifier.py`, `agent_tools.py`,
// `semantic_memory.py`, `knowledge_base.py`.
const PIPELINE_LOGS = {
  'Hola, buenos días': [
    ['> POST /whatsapp/webhook', '  Body: "Hola, buenos días"  NumMedia: 0'],
    ['> routes.py → procesar_mensaje_whatsapp()', '  ✓ validaciones OK', '  Sesión: nueva creada'],
    ['> semantic_memory.py → get_context_for_agent()', '  0 memorias relevantes', '                                                   ~80ms'],
    ['> central_classifier.py → classify_message()', '  gpt-4o-mini | [msg + mem(0) + hist(0)]', '  → SALUDO (95%) — Regla 1 match                   ~160ms'],
    ['> routes.py → handler saludo', '  Tono de agente de viajes · emojis 🌴🚤', '  Respuesta generada                               ~140ms'],
    ['> routes.py → post_processing()', '  cierre_ia() → CONVERSACION_ABIERTA', '  sumar_tokens(280, chatbot_id=63)'],
    ['> Twilio.messages.create()', '  to: whatsapp:+573021168888', '  ✓ Enviado                                  Total: ~760ms'],
  ],
  '¿Qué salidas hay para mañana?': [
    ['> POST /whatsapp/webhook', '  Body: "¿Qué salidas hay para mañana?"'],
    ['> routes.py → procesar_mensaje_whatsapp()', '  ✓ validaciones OK', '  Sesión: activa encontrada'],
    ['> semantic_memory.py → get_context_for_agent()', '  1 memoria: prefiere Playa Blanca (sim: 0.74)', '  Contexto inyectado: 60 tokens                   ~110ms'],
    ['> central_classifier.py → classify_message()', '  gpt-4o-mini | [msg + mem(1) + hist(2)]', '  → TOOL_USE (97%) — Regla 2 match                 ~180ms'],
    ['> agent_tools.py → consultar_salidas_disponibles()', '  Query: salidas + paquetes + cupos', '  2 salidas con cupo para mañana                  ~240ms'],
    ['> routes.py → post_processing()', '  cierre_ia() → CONVERSACION_ABIERTA', '  sumar_tokens(1240, chatbot_id=63)'],
    ['> Twilio.messages.create()', '  to: whatsapp:+573021168888', '  ✓ Enviado                                 Total: ~1180ms'],
  ],
  'Quiero reservar 4 personas para el sábado': [
    ['> POST /whatsapp/webhook', '  Body: "Quiero reservar 4 personas para el sábado"'],
    ['> routes.py → procesar_mensaje_whatsapp()', '  ✓ validaciones OK', '  Sesión: activa encontrada'],
    ['> semantic_memory.py → get_context_for_agent()', '  0 memorias relevantes', '                                                   ~85ms'],
    ['> central_classifier.py → classify_message()', '  gpt-4o-mini | [msg + mem(0) + hist(3)]', '  → TOOL_USE (98%) — Regla 2 match                 ~190ms'],
    ['> agent_tools.py → crear_reservacion()', '  SELECT FOR UPDATE sobre la salida', '  4 cupos apartados · código ENT-20260905-018     ~320ms'],
    ['> routes.py → post_processing()', '  Comprobante formateado para WhatsApp', '  sumar_tokens(1480, chatbot_id=63)'],
    ['> Twilio.messages.create()', '  to: whatsapp:+573021168888', '  ✓ Enviado                                 Total: ~1420ms'],
  ],
  '¿Qué me recomendaste la vez pasada?': [
    ['> POST /whatsapp/webhook', '  Body: "¿Qué me recomendaste la vez pasada?"'],
    ['> routes.py → procesar_mensaje_whatsapp()', '  ✓ validaciones OK', '  Sesión: activa encontrada'],
    ['> semantic_memory.py → get_context_for_agent()', '  1 memoria: prefirió Barú (sim: 0.82)', '  Contexto inyectado: 60 tokens                   ~100ms'],
    ['> central_classifier.py → classify_message()', '  gpt-4o-mini | [msg + mem(1) + hist(2)]', '  → MEMORIA (92%) — Regla 3 match                  ~170ms'],
    ['> routes.py → handler memoria', '  semantic_memory.search(umbral: 0.35) → 1 resultado', '  gpt-4o-mini → respuesta contextual              ~200ms'],
    ['> routes.py → post_processing()', '  cierre_ia() → CONVERSACION_ABIERTA', '  sumar_tokens(290, chatbot_id=63)'],
    ['> Twilio.messages.create()', '  to: whatsapp:+573021168888', '  ✓ Enviado                                  Total: ~920ms'],
  ],
  '¿Cuánto cuesta el pasadía?': [
    ['> POST /whatsapp/webhook', '  Body: "¿Cuánto cuesta el pasadía?"'],
    ['> routes.py → procesar_mensaje_whatsapp()', '  ✓ validaciones OK', '  Sesión: activa encontrada'],
    ['> semantic_memory.py → get_context_for_agent()', '  0 memorias relevantes', '                                                   ~90ms'],
    ['> central_classifier.py → classify_message()', '  gpt-4o-mini | [msg + mem(0) + hist(1) + kb(3)]', '  → INFORMACION (96%) — Regla 4 match              ~180ms'],
    ['> knowledge_base.py → search()', '  Capa 1: IA selecciona 2 entries relevantes', '  gpt-4o-mini → respuesta con precios e inclusiones ~250ms'],
    ['> routes.py → post_processing()', '  cierre_ia() → CONVERSACION_ABIERTA', '  sumar_tokens(380, chatbot_id=63)'],
    ['> Twilio.messages.create()', '  to: whatsapp:+573021168888', '  ✓ Enviado                                  Total: ~980ms'],
  ],
  'Ok, muchas gracias': [
    ['> POST /whatsapp/webhook', '  Body: "Ok, muchas gracias"  NumMedia: 0'],
    ['> routes.py → procesar_mensaje_whatsapp()', '  ✓ validaciones OK', '  Sesión: activa encontrada'],
    ['> semantic_memory.py → get_context_for_agent()', '  0 memorias relevantes', '                                                   ~80ms'],
    ['> central_classifier.py → classify_message()', '  gpt-4o-mini | [msg + mem(0) + hist(4)]', '  → CONVERSACION (89%) — Regla 5 (default)         ~160ms'],
    ['> routes.py → handler conversación', '  gpt-4o-mini con personalidad del chatbot', '  Respuesta natural generada                       ~150ms'],
    ['> routes.py → _procesar_cierre_si_aplica()', '  cierre_ia() → CIERRE_TEMPORAL ⚡', '  sesion.cerrada + conversaciones_totales += 1', '  sumar_tokens(310, chatbot_id=63)'],
    ['> Twilio.messages.create()', '  to: whatsapp:+573021168888', '  ✓ Enviado                                  Total: ~780ms'],
  ],
  'Necesito hablar con alguien real': [
    ['> POST /whatsapp/webhook', '  Body: "Necesito hablar con alguien real"'],
    ['> routes.py → procesar_mensaje_whatsapp()', '  ✓ validaciones OK', '  Sesión: activa encontrada'],
    ['> semantic_memory.py → get_context_for_agent()', '  0 memorias relevantes', '                                                   ~90ms'],
    ['> central_classifier.py → classify_message()', '  gpt-4o-mini | [msg + mem(0) + hist(2)]', '  → ESCALACION (94%) — Regla 6 match               ~170ms'],
    ['> agent_tools.py → transferir_a_humano()', '  escalacion_estado = "pendiente"', '  Notificación al coordinador'],
    ['> routes.py → post_processing()', '  ConversacionLog: resultado="escalada"', '  sumar_tokens(420, chatbot_id=63)'],
    ['> Twilio.messages.create() x2', '  to: +573021168888 (turista)', '  to: +573001112233 (coordinador — notificación)', '  ✓ Enviados                                 Total: ~950ms'],
  ],
  '📷 [Foto del comprobante de pago]': [
    ['> POST /whatsapp/webhook', '  NumMedia: 1  ContentType: image/jpeg', '  MediaUrl: https://api.twilio.com/2010-04/...'],
    ['> routes.py → procesar_mensaje_whatsapp()', '  ✓ validaciones OK', '  Sesión: activa  Tipo detectado: IMAGEN'],
    ['> semantic_memory.py → get_context_for_agent()', '  1 memoria: reserva pendiente de pago (sim: 0.79)', '  Contexto inyectado: 50 tokens                   ~100ms'],
    ['> comprobante_ocr.py → leer_comprobante()', '  Modelo: Claude (Vision)', '  Imagen descargada (234 KB) → monto y referencia', '  $400.000 · ref 0000774                          ~2400ms'],
    ['> routes.py → asociar_voucher()', '  Voucher vinculado a ENT-20260905-018', '  Saldo recalculado: $400.000                     ~180ms'],
    ['> routes.py → post_processing()', '  sumar_tokens(2100, chatbot_id=63)'],
    ['> Twilio.messages.create()', '  to: whatsapp:+573021168888', '  ✓ Enviado                                 Total: ~3100ms'],
  ],
  'Mándame fotos del Cogela Suave': [
    ['> POST /whatsapp/webhook', '  Body: "Mándame fotos del Cogela Suave"'],
    ['> routes.py → procesar_mensaje_whatsapp()', '  ✓ validaciones OK', '  Sesión: activa encontrada'],
    ['> semantic_memory.py → get_context_for_agent()', '  0 memorias relevantes', '                                                   ~85ms'],
    ['> central_classifier.py → classify_message()', '  gpt-4o-mini | [msg + mem(0) + hist(2)]', '  → TOOL_USE (95%) — enviar_imagenes_embarcacion   ~175ms'],
    ['> agent_tools.py → enviar_imagenes_embarcacion()', '  Nombre normalizado: "cojela suave" → Cogela Suave', '  3 imágenes encontradas                          ~200ms'],
    ['> routes.py → post_processing()', '  sumar_tokens(340, chatbot_id=63)'],
    ['> Twilio.messages.create() x3', '  to: whatsapp:+573021168888 (media)', '  ✓ Enviados                                Total: ~1650ms'],
  ],
  'Cuánto cupo tengo disponible?': [
    ['> POST /whatsapp/webhook', '  Body: "Cuánto cupo tengo disponible?"'],
    ['> routes.py → procesar_mensaje_whatsapp()', '  Número en agencia_contactos → rama AGENCIA', '  _agencia_detectada = true · privacidad auto-aceptada'],
    ['> semantic_memory.py', '  omitida — la rama agencia no usa memoria de turista'],
    ['> central_classifier.py → classify_message()', '  gpt-4o-mini | prompt bifurcado (agencia)', '  → TOOL_USE (97%) — consultar_cupo_agencia        ~170ms'],
    ['> agent_tools.py → consultar_cupo_agencia()', '  Cupo $5.000.000 · utilizado $3.200.000', '  Disponible $1.800.000                           ~150ms'],
    ['> routes.py → post_processing()', '  Sin clasificador de cierre (rama agencia)', '  sumar_tokens(390, chatbot_id=63)'],
    ['> Twilio.messages.create()', '  to: whatsapp:+573104445566 (vendedor)', '  ✓ Enviado                                  Total: ~840ms'],
  ],
  '¿Dónde queda el muelle?': [
    ['> POST /whatsapp/webhook', '  Body: "¿Dónde queda el muelle?"  NumMedia: 0'],
    ['> routes.py → procesar_mensaje_whatsapp()', '  ✓ validaciones OK', '  Sesión: activa encontrada'],
    ['> semantic_memory.py → get_context_for_agent()', '  0 memorias relevantes', '                                                   ~85ms'],
    ['> central_classifier.py → classify_message()', '  gpt-4o-mini | [msg + cache(5) + examples(3)]', '  📝 Few-shot match: "muelle" → INFORMACION', '  → INFORMACION (97%) — example guided              ~160ms'],
    ['> knowledge_base.py → search()', '  Capa 1: IA → 1 entry "punto de embarque"', '  gpt-4o-mini → dirección e instrucciones          ~210ms'],
    ['> routes.py → post_processing()', '  cierre_ia() → CONVERSACION_ABIERTA', '  sumar_tokens(350, chatbot_id=63)'],
    ['> Twilio.messages.create()', '  to: whatsapp:+573021168888', '  ✓ Enviado                                  Total: ~860ms'],
  ],
};

export default function AdminCorrections() {
  // const navigate = useNavigate();
  const userId = localStorage.getItem('user_id');

  // Data state
  const [suggestions, setSuggestions] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);

  // UI state
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState({ current: 0, total: 0, suggestions: 0 });
  const [expandedId, setExpandedId] = useState(null);
  const [filterEstado, setFilterEstado] = useState('pendiente');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success', undoId: null, undoAction: null });
  const [actionLoading, setActionLoading] = useState(null);
  const [showCategories, setShowCategories] = useState(false);

  // Feature #1: Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Feature #3: Sort by confidence
  const [sortByConfianza, setSortByConfianza] = useState(false);

  // Tabs
  const [activeTab, setActiveTab] = useState(0);

  // Pipe phases definition
  const PIPE_PHASES = [
    { icon: '📩', label: 'Entrada', color: '#0ea5e9', desc: 'Twilio webhook', resultKey: 'entrada' },
    { icon: '🔒', label: 'Gates', color: '#f59e0b', desc: 'Privacidad + sesión', resultKey: 'gates' },
    { icon: '🧠', label: 'Memoria', color: '#8b5cf6', desc: 'Embeddings semánticos', resultKey: 'memoria' },
    { icon: '⚡', label: 'Clasificador', color: '#1e293b', desc: 'GPT-4o-mini → 6 categorías', resultKey: null },
    { icon: '🎯', label: 'Handler', color: '#10b981', desc: 'Rama especializada', resultKey: 'handler' },
    { icon: '💾', label: 'Post-proceso', color: '#6366f1', desc: 'Memoria + tracking', resultKey: 'post' },
  ];

  // Pipeline simulation
  const [simulating, setSimulating] = useState(false);
  const [simPhase, setSimPhase] = useState(-1);
  const [simScenario, setSimScenario] = useState(null);
  const [simComplete, setSimComplete] = useState(false);
  const simTimersRef = useRef([]);

  const isSimActive = simulating || simComplete;

  const getPhaseState = (phaseIndex) => {
    if (!isSimActive) return 'default';
    if (simComplete) return 'completed';
    if (simPhase < phaseIndex) return 'waiting';
    if (simPhase === phaseIndex) return 'processing';
    return 'completed';
  };

  const runSimulation = (scenario) => {
    simTimersRef.current.forEach(t => clearTimeout(t));
    simTimersRef.current = [];
    setSimScenario(scenario);
    setSimulating(true);
    setSimComplete(false);
    setSimPhase(-1);
    [400, 1200, 2000, 2800, 3800, 4600].forEach((delay, i) => {
      simTimersRef.current.push(setTimeout(() => setSimPhase(i), delay));
    });
    simTimersRef.current.push(setTimeout(() => { setSimComplete(true); setSimulating(false); }, 5400));
  };

  const resetSimulation = () => {
    simTimersRef.current.forEach(t => clearTimeout(t));
    simTimersRef.current = [];
    setSimulating(false);
    setSimComplete(false);
    setSimPhase(-1);
    setSimScenario(null);
  };

  // Classifier Examples (few-shot tuning global)
  const [examples, setExamples] = useState([]);
  const [examplesCount, setExamplesCount] = useState(0);
  const [examplesMax, setExamplesMax] = useState(30);
  const [exLoading, setExLoading] = useState(false);
  const [exSaving, setExSaving] = useState(false);
  const [exMsg, setExMsg] = useState('');
  const [exCat, setExCat] = useState('INFORMACION');
  const [exExpl, setExExpl] = useState('');

  // Refs
  const undoTimerRef = useRef(null);
  const examplesPanelRef = useRef(null);
  const terminalRef = useRef(null);

  // Load data
  const loadStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/corrections/stats?user_id=${userId}`);
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (e) {
      console.error('Error loading stats:', e);
    }
  };

  const loadSuggestions = async () => {
    try {
      setLoading(true);
      let url = `${API_BASE}/admin/corrections/suggestions?user_id=${userId}&limit=200`;
      if (filterEstado) url += `&estado=${filterEstado}`;

      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Error loading suggestions:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadExamples = async () => {
    try {
      setExLoading(true);
      const res = await fetch(`${API_BASE}/admin/corrections/classifier-examples?user_id=${userId}`);
      if (res.ok) {
        const data = await res.json();
        setExamples(data.examples || []);
        setExamplesCount(data.count || 0);
        setExamplesMax(data.max || 30);
      }
    } catch (e) {
      console.error('Error loading examples:', e);
    } finally {
      setExLoading(false);
    }
  };

  const addExample = async () => {
    if (!exMsg.trim()) return;
    try {
      setExSaving(true);
      const res = await fetch(`${API_BASE}/admin/corrections/classifier-examples?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          example_message: exMsg.trim(),
          expected_category: exCat,
          explanation: exExpl.trim() || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        setSnackbar({ open: true, message: data.detail || 'Error', severity: 'error' });
        return;
      }
      setExMsg('');
      setExExpl('');
      loadExamples();
      setSnackbar({ open: true, message: 'Ejemplo agregado', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Error creando ejemplo', severity: 'error' });
    } finally {
      setExSaving(false);
    }
  };

  const deleteExample = async (id) => {
    const exToDelete = examples.find((ex) => ex.id === id);
    try {
      const res = await fetch(`${API_BASE}/admin/corrections/classifier-examples/${id}?user_id=${userId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setExamples((prev) => prev.filter((ex) => ex.id !== id));
        setExamplesCount((c) => c - 1);
        setSnackbar({
          open: true,
          message: `Ejemplo eliminado: "${exToDelete?.example_message?.substring(0, 40) || '...'}"`,
          severity: 'warning',
          undoId: null,
          undoAction: exToDelete ? () => reAddExample(exToDelete) : null,
        });
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Error eliminando', severity: 'error' });
    }
  };

  const reAddExample = async (ex) => {
    try {
      const res = await fetch(`${API_BASE}/admin/corrections/classifier-examples?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          example_message: ex.example_message,
          expected_category: ex.expected_category,
          explanation: ex.explanation || null,
        }),
      });
      if (res.ok) {
        loadExamples();
        setSnackbar({ open: true, message: 'Ejemplo restaurado', severity: 'success' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Error restaurando', severity: 'error' });
    }
  };

  const prefillFromSuggestion = (suggestion) => {
    setExMsg(suggestion.mensaje_original || '');
    setExCat(suggestion.clasificacion_sugerida || 'INFORMACION');
    setExExpl(suggestion.razon ? suggestion.razon.substring(0, 255) : '');
    setActiveTab(1);
    setTimeout(() => {
      examplesPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  };

  useEffect(() => {
    loadStats();
    loadSuggestions();
    loadExamples();
    return () => { simTimersRef.current.forEach(t => clearTimeout(t)); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedIds(new Set());
    loadSuggestions();
  }, [filterEstado]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (terminalRef.current) {
      setTimeout(() => { terminalRef.current.scrollTop = terminalRef.current.scrollHeight; }, 60);
    }
  }, [simPhase, simComplete]);

  // Sorted suggestions
  const sortedSuggestions = React.useMemo(() => {
    if (!sortByConfianza) return suggestions;
    return [...suggestions].sort((a, b) => b.confianza - a.confianza);
  }, [suggestions, sortByConfianza]);

  // Feature #2: Auto-advance — find next pending after action
  const autoAdvanceToNext = useCallback((currentId) => {
    const currentIndex = sortedSuggestions.findIndex(s => s.id === currentId);
    // Find the next pending suggestion after the current one
    for (let i = currentIndex + 1; i < sortedSuggestions.length; i++) {
      if (sortedSuggestions[i].estado === 'pendiente') {
        setExpandedId(sortedSuggestions[i].id);
        return;
      }
    }
    // If none after, look before
    for (let i = 0; i < currentIndex; i++) {
      if (sortedSuggestions[i].estado === 'pendiente') {
        setExpandedId(sortedSuggestions[i].id);
        return;
      }
    }
    setExpandedId(null);
  }, [sortedSuggestions]);

  // Actions
  const BATCH_SIZE = 5;

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setAnalyzeProgress({ current: 0, total: 0, suggestions: 0 });

    let totalAnalyzed = 0;
    let totalSuggestions = 0;
    let totalToProcess = 0;
    let remaining = 1; // start truthy

    try {
      while (remaining > 0) {
        const res = await fetch(`${API_BASE}/admin/corrections/analyze?user_id=${userId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ limit: BATCH_SIZE })
        });
        const data = await res.json();

        const batchAnalyzed = data.analizadas || 0;
        if (batchAnalyzed === 0) break; // nothing left

        totalAnalyzed += batchAnalyzed;
        totalSuggestions += (data.sugerencias_generadas || 0);
        remaining = data.remaining || 0;

        // On first batch, compute total
        if (totalToProcess === 0) {
          totalToProcess = (data.total_pending_before || batchAnalyzed) ;
        }

        setAnalyzeProgress({
          current: totalAnalyzed,
          total: totalToProcess,
          suggestions: totalSuggestions
        });
      }

      setSnackbar({
        open: true,
        message: `Análisis completado: ${totalAnalyzed} conversaciones, ${totalSuggestions} sugerencias`,
        severity: 'success',
        undoId: null, undoAction: null
      });
      loadStats();
      loadSuggestions();
    } catch (e) {
      setSnackbar({ open: true, message: `Error: ${e.message}`, severity: 'error', undoId: null, undoAction: null });
    } finally {
      setAnalyzing(false);
      setAnalyzeProgress({ current: 0, total: 0, suggestions: 0 });
    }
  };

  // Feature #4: Undo support
  const handleUndo = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/admin/corrections/suggestions/${id}/undo?user_id=${userId}`, {
        method: 'POST'
      });
      if (res.ok) {
        setSnackbar({ open: false, message: '', severity: 'success', undoId: null, undoAction: null });
        loadStats();
        loadSuggestions();
      }
    } catch (e) {
      console.error('Error undoing:', e);
    }
  };

  const showSnackbarWithUndo = (message, severity, id, action) => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setSnackbar({ open: true, message, severity, undoId: id, undoAction: action });
    undoTimerRef.current = setTimeout(() => {
      setSnackbar(prev => ({ ...prev, undoId: null, undoAction: null }));
    }, 5000);
  };

  const handleConfirm = async (id) => {
    setActionLoading(id);
    try {
      const res = await fetch(`${API_BASE}/admin/corrections/suggestions/${id}/confirm?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (res.ok) {
        const data = await res.json();
        showSnackbarWithUndo(data.message, 'success', id, 'confirm');
        autoAdvanceToNext(id);
        loadStats();
        loadSuggestions();
      } else {
        const err = await res.json();
        setSnackbar({ open: true, message: err.detail || 'Error', severity: 'error', undoId: null, undoAction: null });
      }
    } catch (e) {
      setSnackbar({ open: true, message: `Error: ${e.message}`, severity: 'error', undoId: null, undoAction: null });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismiss = async (id) => {
    setActionLoading(id);
    try {
      const res = await fetch(`${API_BASE}/admin/corrections/suggestions/${id}/dismiss?user_id=${userId}`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        showSnackbarWithUndo(data.message, 'info', id, 'dismiss');
        autoAdvanceToNext(id);
        loadStats();
        loadSuggestions();
      } else {
        const err = await res.json();
        setSnackbar({ open: true, message: err.detail || 'Error', severity: 'error', undoId: null, undoAction: null });
      }
    } catch (e) {
      setSnackbar({ open: true, message: `Error: ${e.message}`, severity: 'error', undoId: null, undoAction: null });
    } finally {
      setActionLoading(null);
    }
  };

  // Feature #1: Bulk actions
  const handleBulkConfirm = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/corrections/bulk-confirm?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds) })
      });
      if (res.ok) {
        const data = await res.json();
        setSnackbar({ open: true, message: data.message, severity: 'success', undoId: null, undoAction: null });
        setSelectedIds(new Set());
        loadStats();
        loadSuggestions();
      }
    } catch (e) {
      setSnackbar({ open: true, message: `Error: ${e.message}`, severity: 'error', undoId: null, undoAction: null });
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDismiss = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/corrections/bulk-dismiss?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds) })
      });
      if (res.ok) {
        const data = await res.json();
        setSnackbar({ open: true, message: data.message, severity: 'info', undoId: null, undoAction: null });
        setSelectedIds(new Set());
        loadStats();
        loadSuggestions();
      }
    } catch (e) {
      setSnackbar({ open: true, message: `Error: ${e.message}`, severity: 'error', undoId: null, undoAction: null });
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkUndo = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/corrections/bulk-undo?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds) })
      });
      if (res.ok) {
        const data = await res.json();
        setSnackbar({ open: true, message: data.message, severity: 'success', undoId: null, undoAction: null });
        setSelectedIds(new Set());
        loadStats();
        loadSuggestions();
      }
    } catch (e) {
      setSnackbar({ open: true, message: `Error: ${e.message}`, severity: 'error', undoId: null, undoAction: null });
    } finally {
      setBulkLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const selectableIds = selectableSuggestions.map(s => s.id);
    if (selectableIds.length === 0) return;
    const allSelected = selectableIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableIds));
    }
  };

  // Selectable = items that match the current filter's actionable state
  const selectableSuggestions = filterEstado === 'pendiente'
    ? sortedSuggestions.filter(s => s.estado === 'pendiente')
    : filterEstado === 'descartada'
      ? sortedSuggestions.filter(s => s.estado === 'descartada')
      : filterEstado === 'confirmada'
        ? sortedSuggestions.filter(s => s.estado === 'confirmada')
        : [];
  const allSelectableSelected = selectableSuggestions.length > 0 && selectableSuggestions.every(s => selectedIds.has(s.id));
  const showBulkBar = (filterEstado === 'pendiente' || filterEstado === 'descartada' || filterEstado === 'confirmada') && selectableSuggestions.length > 0;

  // Render helpers
  const CategoryChip = ({ category, size = 'small' }) => {
    const color = getCatColor(category);
    return (
      <Chip
        label={color.label}
        size={size}
        sx={{
          bgcolor: color.bg,
          color: '#fff',
          fontWeight: 700,
          fontSize: 11,
          height: size === 'small' ? 24 : 28,
        }}
      />
    );
  };

  const StatCard = ({ label, value, icon, gradient, shadowColor }) => (
    <Box sx={{
      p: 2,
      borderRadius: 3,
      background: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
      border: '1px solid #e2e8f0',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: 3,
        background: gradient
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          background: gradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff',
          boxShadow: `0 4px 12px ${shadowColor}`,
          '& svg': { fontSize: 20 }
        }}>
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 600, mt: 0.25 }}>
            {label}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // Feature #5: Progress calculation
  const reviewedToday = (stats.revisadas_hoy || 0);
  const totalPending = (stats.pendientes || 0);
  const totalToReview = reviewedToday + totalPending;
  const progressPercent = totalToReview > 0 ? Math.round((reviewedToday / totalToReview) * 100) : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9' }}>
      {/* Sticky top section */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 10,
        bgcolor: '#f1f5f9',
        px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 0,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1e293b' }}>
            Correcciones IA
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            Revisión y aprendizaje de errores de clasificación
          </Typography>
        </Box>
        <Tooltip title="Analizar conversaciones">
          <Button
            variant="contained"
            startIcon={analyzing ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={handleAnalyze}
            disabled={analyzing}
            sx={{
              background: 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(225, 29, 72, 0.4)',
              '&:hover': { boxShadow: '0 8px 20px rgba(225, 29, 72, 0.5)' }
            }}
          >
            {analyzing ? 'Analizando...' : 'Analizar'}
          </Button>
        </Tooltip>
        <Tooltip title="Refrescar">
          <IconButton onClick={() => { loadStats(); loadSuggestions(); loadExamples(); }} sx={{ color: '#64748b' }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Analyze progress banner */}
      {analyzing && (
        <Box sx={{
          mb: 1.5, p: 1.5, borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(225, 29, 72, 0.05) 0%, rgba(190, 18, 60, 0.08) 100%)',
          border: '1px solid rgba(225, 29, 72, 0.15)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <CircularProgress size={18} sx={{ color: '#e11d48' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
              {analyzeProgress.total > 0
                ? `Analizando conversaciones... ${analyzeProgress.current} de ${analyzeProgress.total}`
                : 'Preparando análisis...'}
            </Typography>
            {analyzeProgress.suggestions > 0 && (
              <Chip
                label={`${analyzeProgress.suggestions} sugerencias`}
                size="small"
                sx={{
                  bgcolor: '#dcfce7', color: '#166534',
                  fontWeight: 700, fontSize: 11, height: 22,
                }}
              />
            )}
          </Box>
          {analyzeProgress.total > 0 && (
            <LinearProgress
              variant="determinate"
              value={Math.round((analyzeProgress.current / analyzeProgress.total) * 100)}
              sx={{
                height: 8, borderRadius: 4,
                bgcolor: 'rgba(225, 29, 72, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #e11d48, #be123c)',
                  transition: 'transform 0.5s ease',
                }
              }}
            />
          )}
        </Box>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, val) => setActiveTab(val)}
        sx={{
          minHeight: 40,
          '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
            background: 'linear-gradient(90deg, #e11d48, #be123c)',
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 13,
            minHeight: 40,
            color: '#64748b',
            '&.Mui-selected': { color: '#1e293b' },
          },
        }}
      >
        <Tab
          icon={<PsychologyIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label={`Sugerencias${(stats.pendientes || 0) > 0 ? ` (${stats.pendientes})` : ''}`}
        />
        <Tab
          icon={<SchoolIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label={`Ejemplos${examplesCount > 0 ? ` (${examplesCount})` : ''}`}
        />
        <Tab
          icon={<AccountTreeIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Pipeline"
        />
      </Tabs>

      </Box>{/* End sticky section */}

      {/* ==================== TAB 0: Sugerencias ==================== */}
      {activeTab === 0 && (
      <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, pt: 1.5 }}>

      {/* Stats Cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 1.5,
        mb: 1.5
      }}>
        <StatCard
          label="Pendientes"
          value={stats.pendientes || 0}
          icon={<PendingIcon />}
          gradient="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
          shadowColor="rgba(245, 158, 11, 0.4)"
        />
        <StatCard
          label="Confirmadas hoy"
          value={stats.confirmadas_hoy || 0}
          icon={<CheckCircleIcon />}
          gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
          shadowColor="rgba(16, 185, 129, 0.4)"
        />
        <StatCard
          label="Total aprendidas"
          value={stats.total_aprendidas || 0}
          icon={<SchoolIcon />}
          gradient="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
          shadowColor="rgba(59, 130, 246, 0.4)"
        />
        <StatCard
          label="Tasa confirmación"
          value={`${stats.tasa_confirmacion || 0}%`}
          icon={<TrendingUpIcon />}
          gradient="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
          shadowColor="rgba(139, 92, 246, 0.4)"
        />
      </Box>

      {/* Feature #5: Progress bar */}
      {totalToReview > 0 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5,
          p: 1, px: 1.5, borderRadius: 2,
          background: '#fff',
          border: '1px solid #e2e8f0',
        }}>
          <DoneAllIcon sx={{ fontSize: 18, color: progressPercent === 100 ? '#10b981' : '#64748b' }} />
          <Box sx={{ flex: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                height: 6, borderRadius: 3,
                bgcolor: '#e2e8f0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: progressPercent === 100
                    ? 'linear-gradient(90deg, #10b981, #059669)'
                    : 'linear-gradient(90deg, #f59e0b, #d97706)',
                }
              }}
            />
          </Box>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: progressPercent === 100 ? '#059669' : '#92400e', whiteSpace: 'nowrap' }}>
            {reviewedToday} de {totalToReview} revisadas hoy
          </Typography>
        </Box>
      )}

      {/* Category Legend — collapsible */}
      <Box sx={{ mb: 1.5 }}>
        <Chip
          icon={<InfoOutlinedIcon sx={{ fontSize: '16px !important' }} />}
          label={showCategories ? 'Ocultar categorías' : 'Categorías'}
          size="small"
          onClick={() => setShowCategories(!showCategories)}
          onDelete={() => setShowCategories(!showCategories)}
          deleteIcon={showCategories ? <ExpandLessIcon sx={{ fontSize: '18px !important' }} /> : <ExpandMoreIcon sx={{ fontSize: '18px !important' }} />}
          sx={{
            bgcolor: showCategories ? '#1e293b' : '#fff',
            color: showCategories ? '#fff' : '#475569',
            fontWeight: 600, fontSize: 12,
            border: showCategories ? 'none' : '1px solid #e2e8f0',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '& .MuiChip-deleteIcon': { color: showCategories ? '#94a3b8' : '#94a3b8' },
            '&:hover': { bgcolor: showCategories ? '#334155' : 'rgba(255,255,255,1)' },
          }}
        />
        <Collapse in={showCategories}>
          <Box sx={{
            mt: 1, p: 1.5, borderRadius: 2,
            background: '#fff',
            border: '1px solid #e2e8f0',
            display: 'flex', flexWrap: 'wrap', gap: 1,
          }}>
            {Object.entries(CATEGORY_COLORS).filter(([k]) => k !== 'DESCONOCIDA').map(([key, cat]) => (
              <Box key={key} sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                px: 1.25, py: 0.5, borderRadius: 1.5,
                bgcolor: `${cat.bg}10`, border: `1px solid ${cat.bg}25`,
              }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cat.bg, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: cat.bg, whiteSpace: 'nowrap' }}>
                  {cat.label}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                  {cat.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>

      {/* Filters + Sort + Bulk */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={filterEstado}
          exclusive
          onChange={(e, val) => { if (val !== null) setFilterEstado(val); }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none', fontWeight: 600, fontSize: 12, px: 2,
              borderColor: 'rgba(0,0,0,0.12)',
              '&.Mui-selected': {
                bgcolor: '#1e293b', color: '#fff',
                '&:hover': { bgcolor: '#334155' }
              }
            }
          }}
        >
          <ToggleButton value="pendiente">Pendientes</ToggleButton>
          <ToggleButton value="">Todas</ToggleButton>
          <ToggleButton value="confirmada">Confirmadas</ToggleButton>
          <ToggleButton value="descartada">Descartadas</ToggleButton>
        </ToggleButtonGroup>

        {/* Feature #3: Sort toggle */}
        <Tooltip title={sortByConfianza ? 'Orden por fecha' : 'Orden por confianza (mayor primero)'}>
          <Chip
            icon={<SortIcon sx={{ fontSize: '16px !important' }} />}
            label={sortByConfianza ? 'Confianza ↓' : 'Fecha'}
            size="small"
            onClick={() => setSortByConfianza(!sortByConfianza)}
            sx={{
              bgcolor: sortByConfianza ? '#1e293b' : 'transparent',
              color: sortByConfianza ? '#fff' : '#64748b',
              fontWeight: 600, fontSize: 11,
              border: sortByConfianza ? 'none' : '1px solid #e2e8f0',
              cursor: 'pointer',
              '& .MuiChip-icon': { color: sortByConfianza ? '#94a3b8' : '#94a3b8' },
              '&:hover': { bgcolor: sortByConfianza ? '#334155' : '#f1f5f9' }
            }}
          />
        </Tooltip>

        <Typography sx={{ fontSize: 13, color: '#64748b', ml: 'auto' }}>
          {suggestions.length} de {total} sugerencias
        </Typography>
      </Box>

      {/* Feature #1: Bulk action bar */}
      {showBulkBar && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mt: 1,
          p: 0.75, px: 1, borderRadius: 2,
          background: selectedIds.size > 0 ? 'rgba(245, 158, 11, 0.08)' : 'transparent',
          border: selectedIds.size > 0 ? '1px solid rgba(245, 158, 11, 0.2)' : '1px solid transparent',
          transition: 'all 0.2s ease',
        }}>
          <Checkbox
            checked={allSelectableSelected}
            indeterminate={selectedIds.size > 0 && !allSelectableSelected}
            onChange={toggleSelectAll}
            size="small"
            sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: 18 } }}
          />
          <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
            {selectedIds.size > 0 ? `${selectedIds.size} seleccionadas` : 'Seleccionar'}
          </Typography>

          {selectedIds.size > 0 && filterEstado === 'pendiente' && (
            <>
              <Button
                size="small"
                variant="contained"
                startIcon={bulkLoading ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                onClick={handleBulkConfirm}
                disabled={bulkLoading}
                sx={{
                  ml: 1,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 11,
                  py: 0.5, px: 1.5,
                  minHeight: 0,
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                }}
              >
                Confirmar ({selectedIds.size})
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={bulkLoading ? <CircularProgress size={14} /> : <CancelIcon />}
                onClick={handleBulkDismiss}
                disabled={bulkLoading}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 11,
                  py: 0.5, px: 1.5,
                  minHeight: 0,
                  borderColor: '#e2e8f0', color: '#64748b',
                  '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
                }}
              >
                Descartar ({selectedIds.size})
              </Button>
            </>
          )}

          {selectedIds.size > 0 && (filterEstado === 'descartada' || filterEstado === 'confirmada') && (
            <Button
              size="small"
              variant="contained"
              startIcon={bulkLoading ? <CircularProgress size={14} color="inherit" /> : <UndoIcon />}
              onClick={handleBulkUndo}
              disabled={bulkLoading}
              sx={{
                ml: 1,
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 11,
                py: 0.5, px: 1.5,
                minHeight: 0,
                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
              }}
            >
              Revertir a pendiente ({selectedIds.size})
            </Button>
          )}
        </Box>
      )}

      {/* Suggestions List */}
      <Box sx={{ pt: 1.5 }}>
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={40} sx={{ color: '#e11d48' }} />
          <Typography sx={{ mt: 2, color: '#64748b', fontSize: 14 }}>Cargando sugerencias...</Typography>
        </Box>
      ) : suggestions.length === 0 ? (
        <Box sx={{
          p: 5, textAlign: 'center',
          background: '#fff',
          borderRadius: 4,
          border: '1px solid #e2e8f0'
        }}>
          <PsychologyIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
          <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600 }}>
            {filterEstado === 'pendiente' ? 'No hay sugerencias pendientes' : 'No hay sugerencias'}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 13, mt: 0.5 }}>
            {filterEstado === 'pendiente'
              ? 'Usa el botón "Analizar" para buscar posibles errores en las conversaciones'
              : 'Cambia los filtros para ver otras sugerencias'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {sortedSuggestions.map((s) => {
            const isExpanded = expandedId === s.id;
            const isPending = s.estado === 'pendiente';
            const isConfirmed = s.estado === 'confirmada';
            const isSelected = selectedIds.has(s.id);
            const actualColor = getCatColor(s.clasificacion_actual);
            const suggestedColor = getCatColor(s.clasificacion_sugerida);

            return (
              <Box
                key={s.id}
                sx={{
                  borderRadius: 3,
                  background: isSelected
                    ? 'rgba(245, 158, 11, 0.06)'
                    : '#fff',
                  boxShadow: isSelected
                    ? '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(245, 158, 11, 0.1)'
                    : '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
                  border: isSelected
                    ? '1px solid rgba(245, 158, 11, 0.4)'
                    : isPending
                      ? '1px solid rgba(245, 158, 11, 0.3)'
                      : '1px solid #e2e8f0',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, right: 0,
                    height: 3,
                    background: isPending
                      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                      : isConfirmed
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                  },
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 8px 28px rgba(0, 0, 0, 0.12)'
                  }
                }}
              >
                {/* Card Header */}
                <Box
                  sx={{ p: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  {/* Feature #1: Checkbox for actionable items */}
                  {showBulkBar && (isPending || s.estado === filterEstado) && (
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => { e.stopPropagation(); toggleSelect(s.id); }}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: 18 } }}
                    />
                  )}

                  <Box
                    sx={{ flex: 1, minWidth: 0 }}
                    onClick={() => setExpandedId(isExpanded ? null : s.id)}
                  >
                    <Typography sx={{
                      fontSize: 14, fontWeight: 700, color: '#1e293b', mb: 0.5,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      "{s.mensaje_original}"
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <CategoryChip category={s.clasificacion_actual} />
                      <Typography sx={{ fontSize: 13, color: '#94a3b8', fontWeight: 700 }}>→</Typography>
                      <CategoryChip category={s.clasificacion_sugerida} />
                      <Chip
                        label={`${Math.round(s.confianza * 100)}%`}
                        size="small"
                        sx={{
                          bgcolor: s.confianza >= 0.9 ? '#dcfce7' : s.confianza >= 0.8 ? '#fef3c7' : '#fee2e2',
                          color: s.confianza >= 0.9 ? '#166534' : s.confianza >= 0.8 ? '#92400e' : '#991b1b',
                          fontWeight: 700, fontSize: 11, height: 24,
                        }}
                      />
                      <Chip
                        label={s.chatbot_nombre}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 11, height: 24, borderColor: '#e2e8f0', color: '#64748b' }}
                      />
                    </Box>
                  </Box>

                  {/* Estado chip */}
                  <Chip
                    icon={isPending ? <PendingIcon /> : isConfirmed ? <CheckCircleIcon /> : <CancelIcon />}
                    label={s.estado}
                    size="small"
                    sx={{
                      bgcolor: isPending ? '#fef3c7' : isConfirmed ? '#dcfce7' : '#f1f5f9',
                      color: isPending ? '#92400e' : isConfirmed ? '#166534' : '#64748b',
                      fontWeight: 700, fontSize: 11,
                      '& .MuiChip-icon': { fontSize: 16 }
                    }}
                  />

                  <IconButton size="small" onClick={() => setExpandedId(isExpanded ? null : s.id)}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* Expanded Content */}
                <Collapse in={isExpanded}>
                  <Box sx={{ px: 2, pb: 2 }}>
                    {/* Razón */}
                    <Box sx={{
                      p: 1.5, borderRadius: 2, mb: 1.5,
                      bgcolor: '#f8fafc', border: '1px solid #e2e8f0'
                    }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5, textTransform: 'uppercase' }}>
                        Razón del error detectado
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: '#1e293b' }}>
                        {s.razon}
                      </Typography>
                    </Box>

                    {/* Clasificación detallada */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5, textTransform: 'uppercase' }}>
                          NARA clasificó como
                        </Typography>
                        <Box sx={{
                          p: 1, borderRadius: 1.5,
                          bgcolor: `${actualColor.bg}15`, border: `1px solid ${actualColor.bg}40`
                        }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: actualColor.bg }}>
                            {s.clasificacion_actual}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5, textTransform: 'uppercase' }}>
                          Debería ser
                        </Typography>
                        <Box sx={{
                          p: 1, borderRadius: 1.5,
                          bgcolor: `${suggestedColor.bg}15`, border: `1px solid ${suggestedColor.bg}40`
                        }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: suggestedColor.bg }}>
                            {s.clasificacion_sugerida}
                            {s.tipo_sugerido ? ` (${s.tipo_sugerido})` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Contexto de conversación — compacto */}
                    {s.contexto_conversacion && (
                      <Box sx={{
                        p: 1.5, borderRadius: 2, mb: 1.5,
                        bgcolor: '#f8fafc', border: '1px solid #e2e8f0'
                      }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', mb: 0.75, textTransform: 'uppercase' }}>
                          Contexto de la conversación
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          {s.contexto_conversacion.split('\n').filter(l => l.trim()).map((line, idx) => {
                            const isTurista = line.startsWith('Turista:');
                            const isNara = line.startsWith('NARA:');
                            const content = isTurista
                              ? line.replace('Turista:', '').trim()
                              : isNara
                                ? line.replace('NARA:', '').trim()
                                : line.trim();

                            // Detect if this line is the misclassified message
                            const isErrorLine = isTurista && content.length > 0 &&
                              s.mensaje_original && (
                                content === s.mensaje_original ||
                                content.toLowerCase().includes(s.mensaje_original.toLowerCase()) ||
                                s.mensaje_original.toLowerCase().includes(content.toLowerCase())
                              );

                            return (
                              <Box key={idx} sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 0.75,
                                py: 0.4,
                                borderLeft: isErrorLine
                                  ? '3px solid #ef4444'
                                  : isTurista ? '3px solid #0ea5e9' : isNara ? '3px solid #10b981' : '3px solid transparent',
                                pl: 1,
                                bgcolor: isErrorLine
                                  ? 'rgba(239, 68, 68, 0.08)'
                                  : isTurista ? 'rgba(14, 165, 233, 0.05)' : 'transparent',
                                borderRadius: 0.5,
                                position: 'relative',
                              }}>
                                <Typography component="span" sx={{
                                  fontSize: 11, fontWeight: 800, minWidth: 52,
                                  color: isErrorLine ? '#dc2626' : isTurista ? '#0369a1' : isNara ? '#166534' : '#64748b',
                                  flexShrink: 0, lineHeight: 1.6,
                                }}>
                                  {isTurista ? 'Turista' : isNara ? 'NARA' : ''}
                                </Typography>
                                <Typography component="span" sx={{
                                  fontSize: 12, lineHeight: 1.6,
                                  color: isErrorLine ? '#991b1b' : '#1e293b',
                                  fontWeight: isErrorLine ? 700 : isTurista ? 600 : 400,
                                }}>
                                  {content}
                                </Typography>
                                {isErrorLine && (
                                  <Chip
                                    label="error"
                                    size="small"
                                    sx={{
                                      height: 18, fontSize: 9, fontWeight: 800,
                                      bgcolor: '#fca5a5', color: '#991b1b',
                                      ml: 0.5, flexShrink: 0,
                                      '& .MuiChip-label': { px: 0.75 }
                                    }}
                                  />
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    {/* Metadata */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                        Creada: {s.created_at ? new Date(s.created_at).toLocaleString() : '-'}
                      </Typography>
                      {s.reviewed_at && (
                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
                          Revisada: {new Date(s.reviewed_at).toLocaleString()}
                        </Typography>
                      )}
                    </Box>

                    {/* Actions */}
                    {isPending && (
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                        <Button
                          variant="contained"
                          startIcon={actionLoading === s.id ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                          onClick={() => handleConfirm(s.id)}
                          disabled={actionLoading === s.id}
                          sx={{
                            flex: 1,
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: 13,
                            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                            '&:hover': { boxShadow: '0 8px 20px rgba(16, 185, 129, 0.5)' }
                          }}
                        >
                          Confirmar corrección
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={actionLoading === s.id ? <CircularProgress size={16} /> : <CancelIcon />}
                          onClick={() => handleDismiss(s.id)}
                          disabled={actionLoading === s.id}
                          sx={{
                            flex: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: 13,
                            borderColor: '#e2e8f0',
                            color: '#64748b',
                            '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
                          }}
                        >
                          No fue error
                        </Button>
                      </Box>
                    )}

                    {/* Agregar como ejemplo — available for all states */}
                    <Tooltip title="Pre-llena el formulario de ejemplos con este mensaje y categoría">
                      <Button
                        size="small"
                        startIcon={<AutoFixHighIcon />}
                        onClick={() => prefillFromSuggestion(s)}
                        sx={{
                          mt: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: 12,
                          color: '#3b82f6',
                          borderRadius: 1.5,
                          '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.08)' }
                        }}
                      >
                        Agregar como ejemplo de clasificación
                      </Button>
                    </Tooltip>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}
      </Box>
      </Box>
      )}

      {/* ==================== TAB 1: Ejemplos ==================== */}
      {activeTab === 1 && (
      <Box ref={examplesPanelRef} sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, pt: 1.5 }}>

        {/* Description */}
        <Typography sx={{ fontSize: 13, color: '#64748b', mb: 2 }}>
          Enseña al clasificador patrones recurrentes. Estos ejemplos se inyectan en el prompt y benefician a <strong>todos los chatbots</strong>.
          Para entender cómo encajan en el sistema, consulta la pestaña <strong>"Pipeline"</strong>.
        </Typography>

        {/* Add form — card layout */}
        <Box sx={{
          p: 1.5, mb: 2, borderRadius: 2,
          bgcolor: '#fff',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)',
        }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#475569', mb: 1, textTransform: 'uppercase', letterSpacing: 0.3 }}>
            Agregar ejemplo
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
            <TextField
              size="small"
              label="Mensaje del turista"
              placeholder='Ej: "quiero pagar", "tienen estacionamiento"'
              value={exMsg}
              onChange={(e) => setExMsg(e.target.value)}
              inputProps={{ maxLength: 200 }}
              helperText={exMsg.length > 0 ? `${exMsg.length}/200` : ''}
              sx={{
                flex: 3, minWidth: 200,
                '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 1.5, bgcolor: '#fff' },
                '& .MuiInputLabel-root': { fontSize: 12 },
                '& .MuiFormHelperText-root': { fontSize: 10, mt: 0.25 },
              }}
            />
            <FormControl size="small" sx={{ flex: 1, minWidth: 140 }}>
              <InputLabel sx={{ fontSize: 12 }}>Categoría correcta</InputLabel>
              <Select
                value={exCat}
                onChange={(e) => setExCat(e.target.value)}
                label="Categoría correcta"
                sx={{ fontSize: 13, borderRadius: 1.5, bgcolor: '#fff' }}
              >
                {Object.entries(CATEGORY_COLORS).filter(([k]) => k !== 'DESCONOCIDA').map(([key, cat]) => (
                  <MenuItem key={key} value={key} sx={{ fontSize: 13 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cat.bg }} />
                      {cat.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              label="Explicación (opcional)"
              placeholder="Ej: Preguntas de pago son info del negocio"
              value={exExpl}
              onChange={(e) => setExExpl(e.target.value)}
              inputProps={{ maxLength: 255 }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 1.5, bgcolor: '#fff' },
                '& .MuiInputLabel-root': { fontSize: 12 },
              }}
            />
            <Button
              variant="contained"
              startIcon={exSaving ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
              onClick={addExample}
              disabled={!exMsg.trim() || exSaving || examplesCount >= examplesMax}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: 13,
                borderRadius: 1.5, py: 0.85, px: 2.5,
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                whiteSpace: 'nowrap',
                '&:hover': { boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)' },
              }}
            >
              Agregar ejemplo
            </Button>
          </Box>
        </Box>

        {/* Examples list */}
        {exLoading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: '#3b82f6' }} />
          </Box>
        ) : examples.length === 0 ? (
          <Box sx={{
            p: 5, textAlign: 'center',
            background: '#fff',
            borderRadius: 4,
            border: '1px solid #e2e8f0'
          }}>
            <SchoolIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
            <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600 }}>
              No hay ejemplos configurados
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13, mt: 0.5 }}>
              Ve a la pestaña "Sugerencias" y usa "Agregar como ejemplo" en cualquier sugerencia, o agrega ejemplos manualmente arriba.
            </Typography>
          </Box>
        ) : (
          <Box sx={{
            p: 2, borderRadius: 3,
            background: '#fff',
            border: '1px solid #e2e8f0',
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {examples.map((ex) => {
                const catInfo = getCatColor(ex.expected_category);
                return (
                  <Box key={ex.id} sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    py: 0.75, px: 1.25, borderRadius: 1.5,
                    bgcolor: '#f8fafc',
                    border: '1px solid #f1f5f9',
                    transition: 'all 0.15s ease',
                    '&:hover': { bgcolor: '#f1f5f9', borderColor: '#e2e8f0', '& .delete-btn': { opacity: 1 } },
                  }}>
                    <Typography sx={{ fontSize: 13, color: '#1e293b', fontWeight: 500, flex: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      "{ex.example_message}"
                    </Typography>
                    {ex.explanation && (
                      <Typography sx={{ fontSize: 11, color: '#94a3b8', flex: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ex.explanation}
                      </Typography>
                    )}
                    <Chip
                      label={catInfo.label}
                      size="small"
                      sx={{
                        height: 20, fontSize: 10, fontWeight: 700,
                        bgcolor: `${catInfo.bg}18`, color: catInfo.bg,
                        flexShrink: 0,
                      }}
                    />
                    <Tooltip title="Eliminar ejemplo">
                      <IconButton
                        size="small"
                        className="delete-btn"
                        onClick={() => deleteExample(ex.id)}
                        sx={{ opacity: 0.3, transition: 'opacity 0.2s', p: 0.5, '&:hover': { color: '#ef4444' } }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>

            {/* Category distribution + counter */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {Object.entries(
                  examples.reduce((acc, ex) => {
                    acc[ex.expected_category] = (acc[ex.expected_category] || 0) + 1;
                    return acc;
                  }, {})
                ).sort((a, b) => b[1] - a[1]).map(([cat, count]) => {
                  const catInfo = getCatColor(cat);
                  return (
                    <Chip
                      key={cat}
                      label={`${catInfo.label} (${count})`}
                      size="small"
                      sx={{
                        height: 20, fontSize: 10, fontWeight: 600,
                        bgcolor: `${catInfo.bg}12`, color: catInfo.bg,
                        border: `1px solid ${catInfo.bg}25`,
                      }}
                    />
                  );
                })}
              </Box>
              <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>
                {examplesCount}/{examplesMax}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      )}

      {/* ==================== TAB 2: Pipeline ==================== */}
      {activeTab === 2 && (
      <Box sx={{ px: { xs: 1.5, md: 3 }, pb: 3, pt: 1.5 }}>

        {/* Scenario selector */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 13, color: '#64748b', mb: 1 }}>
            {isSimActive
              ? 'Observa cómo el mensaje fluye por la tubería de procesamiento.'
              : 'Selecciona un escenario para simular el flujo completo del pipeline.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            {PIPELINE_SCENARIOS.map((sc) => {
              const catInfo = getCatColor(sc.category);
              const isActive = simScenario?.message === sc.message;
              return (
                <Chip
                  key={sc.message}
                  label={sc.chipLabel}
                  size="small"
                  onClick={() => runSimulation(sc)}
                  disabled={simulating && !isActive}
                  sx={{
                    cursor: 'pointer', fontWeight: 700, fontSize: 11,
                    bgcolor: isActive ? catInfo.bg : `${catInfo.bg}15`,
                    color: isActive ? '#fff' : catInfo.bg,
                    border: `1.5px solid ${isActive ? catInfo.bg : `${catInfo.bg}40`}`,
                    transition: 'all 0.3s ease',
                    '&:hover': { bgcolor: isActive ? catInfo.bg : `${catInfo.bg}25` },
                    '& .MuiChip-label': { px: 1.5 },
                  }}
                />
              );
            })}
            {isSimActive && (
              <Chip
                label="Reiniciar"
                size="small"
                onClick={resetSimulation}
                icon={<RefreshIcon sx={{ fontSize: '14px !important' }} />}
                sx={{
                  cursor: 'pointer', fontWeight: 600, fontSize: 11,
                  bgcolor: '#f1f5f9', color: '#64748b',
                  border: '1px solid #e2e8f0',
                  '& .MuiChip-icon': { color: '#94a3b8' },
                  '&:hover': { bgcolor: '#e2e8f0' },
                }}
              />
            )}
          </Box>
        </Box>

        <Box sx={{
          maxWidth: isSimActive ? { xs: 500, md: 920 } : 500,
          mx: 'auto',
          transition: 'max-width 0.5s ease',
        }}>

          {/* ── JOURNEY BAR ── */}
          {isSimActive && (
            <Box sx={{ mb: 1.5, px: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {[
                  { icon: '📱', label: 'Turista', lit: true },
                  { icon: '☁️', label: 'Twilio', lit: simPhase >= 0 },
                  { icon: '🖥️', label: 'Backend', lit: simPhase >= 1 },
                  { icon: '☁️', label: 'Twilio', lit: simComplete },
                  { icon: '📱', label: 'Entregado', lit: simComplete },
                ].map((node, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && (
                      <Box sx={{
                        flex: 1, height: 2, mx: 0.25,
                        borderRadius: 1,
                        bgcolor: node.lit ? '#10b981' : '#e2e8f0',
                        transition: 'background-color 0.6s ease',
                        position: 'relative', overflow: 'hidden',
                        ...((node.lit && (
                          (idx === 1 && simPhase === 0) ||
                          (idx === 2 && simPhase >= 1 && !simComplete) ||
                          (idx === 3 && simComplete && !simScenario?._journeyDone)
                        )) && {
                          '&::after': {
                            content: '""', position: 'absolute', top: 0, left: 0,
                            width: '35%', height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(16,185,129,0.7), transparent)',
                            animation: 'flowLine 1s linear infinite',
                            '@keyframes flowLine': { '0%': { left: '-35%' }, '100%': { left: '100%' } },
                          },
                        }),
                      }} />
                    )}
                    <Box sx={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.15,
                      opacity: node.lit ? 1 : 0.25,
                      transition: 'all 0.5s ease',
                      transform: node.lit ? 'scale(1)' : 'scale(0.85)',
                    }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: node.lit ? '#f0fdf4' : '#f8fafc',
                        border: `2px solid ${node.lit ? '#10b981' : '#d1d5db'}`,
                        transition: 'all 0.5s ease',
                        ...(node.lit && idx === 2 && !simComplete && {
                          animation: 'jPulse 2s ease-in-out infinite',
                          '@keyframes jPulse': { '0%,100%': { boxShadow: '0 0 0 0 rgba(16,185,129,0.3)' }, '50%': { boxShadow: '0 0 0 5px rgba(16,185,129,0)' } },
                        }),
                      }}>
                        {simComplete && idx === 4 ? (
                          <CheckCircleIcon sx={{ fontSize: 14, color: '#10b981' }} />
                        ) : (
                          <Typography sx={{ fontSize: 12, lineHeight: 1 }}>{node.icon}</Typography>
                        )}
                      </Box>
                      <Typography sx={{
                        fontSize: 7, fontWeight: 700, letterSpacing: 0.3,
                        color: node.lit ? '#475569' : '#cbd5e1',
                        textTransform: 'uppercase',
                        transition: 'color 0.5s',
                      }}>
                        {node.label}
                      </Typography>
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}

          {/* ── INPUT BUBBLE ── */}
          {isSimActive && simScenario && (
            <Box sx={{
              mx: 'auto', maxWidth: 320, mb: 0.75,
              px: 1.5, py: 0.75, borderRadius: '14px 14px 4px 14px',
              bgcolor: simScenario.contentType === 'image' ? '#dbeafe'
                : simScenario.contentType === 'document' ? '#fef9c3'
                : '#dcf8c6',
              boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
              animation: 'slideInRight 0.4s ease-out',
              '@keyframes slideInRight': { from: { opacity: 0, transform: 'translateX(20px)' }, to: { opacity: 1, transform: 'translateX(0)' } },
            }}>
              <Typography sx={{ fontSize: 12, color: '#1e293b', fontWeight: 500 }}>
                {simScenario.message}
              </Typography>
              <Typography sx={{ fontSize: 9, color: '#6b7280', textAlign: 'right', mt: 0.25 }}>
                Turista · ahora
              </Typography>
            </Box>
          )}

          {/* ═══ TWO-COLUMN LAYOUT ═══ */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: isSimActive ? 'row' : 'column' },
            gap: isSimActive ? { xs: 0, md: 2.5 } : 0,
            alignItems: 'flex-start',
          }}>
          {/* ═══ LEFT: PIPELINE ═══ */}
          <Box sx={{
            flex: isSimActive ? { md: '0 0 52%' } : '1 1 100%',
            maxWidth: isSimActive ? { xs: '100%', md: '52%' } : '100%',
            minWidth: 0, width: '100%',
          }}>

          {/* Entry funnel */}
          {isSimActive && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
              <Box sx={{
                width: 0, height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: `10px solid ${simPhase >= 0 ? '#10b981' : '#cbd5e1'}`,
                transition: 'border-top-color 0.5s',
                filter: simPhase >= 0 ? 'drop-shadow(0 2px 4px rgba(16,185,129,0.4))' : 'none',
              }} />
            </Box>
          )}

          {/* ═══ THE TUBE ═══ */}
          <Box sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: '#fff',
            boxShadow: isSimActive
              ? '0 8px 32px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.04)'
              : '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid rgba(0,0,0,0.06)',
            transition: 'box-shadow 0.5s',
          }}>
            {PIPE_PHASES.map((phase, i) => {
              const state = getPhaseState(i);
              const isLast = i === PIPE_PHASES.length - 1;
              const isClassifier = i === 3;

              return (
                <Box key={phase.label} sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1.25,
                  px: 1.75,
                  py: isClassifier && isSimActive && state !== 'waiting' && state !== 'default' ? 1.1 : 0.85,
                  borderBottom: !isLast ? '1px solid rgba(0,0,0,0.04)' : 'none',
                  bgcolor: state === 'processing' ? `${phase.color}06` : 'transparent',
                  transition: 'all 0.5s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  borderLeft: `5px solid ${
                    state === 'completed' || state === 'processing' ? phase.color : '#e8ecf0'
                  }`,
                  ...(state === 'processing' && {
                    '&::after': {
                      content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                      background: `linear-gradient(90deg, transparent 0%, ${phase.color}08 50%, transparent 100%)`,
                      animation: 'shimmer 2s ease-in-out infinite',
                      '@keyframes shimmer': { '0%': { transform: 'translateX(-100%)' }, '100%': { transform: 'translateX(100%)' } },
                    },
                  }),
                }}>
                  {/* Node circle */}
                  <Box sx={{
                    width: 28, height: 28, borderRadius: '50%', mt: 0.15,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    background: state === 'completed' ? phase.color
                      : state === 'processing' ? `${phase.color}12`
                      : '#f8fafc',
                    border: `2.5px solid ${
                      state === 'completed' || state === 'processing' ? phase.color : '#d1d5db'
                    }`,
                    transition: 'all 0.5s ease',
                    ...(state === 'processing' && {
                      boxShadow: `0 0 0 4px ${phase.color}15, 0 0 16px ${phase.color}40`,
                      animation: 'nodePulse 2s ease-in-out infinite',
                      '@keyframes nodePulse': {
                        '0%, 100%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.15)' },
                      },
                    }),
                  }}>
                    {state === 'completed' ? (
                      <CheckCircleIcon sx={{ fontSize: 15, color: '#fff' }} />
                    ) : state === 'processing' ? (
                      <CircularProgress size={13} sx={{ color: phase.color }} />
                    ) : (
                      <Typography sx={{ fontSize: 14, lineHeight: 1 }}>{phase.icon}</Typography>
                    )}
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography sx={{
                        fontSize: 12, fontWeight: 700,
                        color: state === 'waiting' ? '#c8cdd3' : '#1e293b',
                        transition: 'color 0.5s',
                      }}>
                        {phase.label}
                      </Typography>

                      {/* Default description (no simulation) */}
                      {!isSimActive && (
                        <Typography sx={{ fontSize: 10, color: '#94a3b8', ml: 'auto' }}>
                          {phase.desc}
                        </Typography>
                      )}

                      {/* Processing indicator */}
                      {state === 'processing' && (
                        <Typography sx={{
                          fontSize: 10, color: phase.color, fontWeight: 600, ml: 'auto',
                          animation: 'fadeIn 0.3s',
                          '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                        }}>
                          procesando...
                        </Typography>
                      )}

                      {/* Completed result (non-classifier) */}
                      {state === 'completed' && simScenario && phase.resultKey && (
                        <Typography sx={{
                          fontSize: 10, color: '#64748b', fontWeight: 600, ml: 'auto',
                          maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          animation: 'fadeIn 0.3s',
                          '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                        }}>
                          {simScenario[phase.resultKey]}
                        </Typography>
                      )}

                      {/* Completed classifier result (special) */}
                      {isClassifier && state === 'completed' && simScenario && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', animation: 'fadeIn 0.3s', '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } } }}>
                          {simScenario.clasificador ? (
                            <>
                              <Chip label={getCatColor(simScenario.clasificador).label} size="small" sx={{
                                height: 18, fontSize: 9, fontWeight: 800,
                                bgcolor: getCatColor(simScenario.clasificador).bg, color: '#fff',
                              }} />
                              <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700 }}>
                                {simScenario.confianza}%
                              </Typography>
                            </>
                          ) : (
                            <Typography sx={{ fontSize: 10, color: '#8b5cf6', fontWeight: 700 }}>
                              {simScenario.classifierModel || 'Procesado'}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* Classifier note (correction cache, examples, image, document) */}
                    {isClassifier && state === 'completed' && simScenario?.classifierNote && (
                      <Typography sx={{
                        fontSize: 9, color: '#64748b', mt: 0.25, lineHeight: 1.3,
                        fontStyle: 'italic',
                        animation: 'fadeIn 0.4s',
                        '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
                      }}>
                        {simScenario.classifierNote}
                      </Typography>
                    )}

                    {/* Classifier category chips row — only for text-based with classification */}
                    {isClassifier && isSimActive && simScenario?.clasificador && (!simScenario?.contentType || simScenario.contentType === 'text') && (state === 'completed' || state === 'processing') && (
                      <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', mt: 0.6 }}>
                        {Object.entries(CATEGORY_COLORS).filter(([k]) => k !== 'DESCONOCIDA').map(([key, cat]) => {
                          const active = state === 'completed' && key === simScenario?.clasificador;
                          return (
                            <Chip key={key} label={cat.label} size="small" sx={{
                              height: 18, fontSize: 8, fontWeight: 700,
                              bgcolor: active ? cat.bg : 'transparent',
                              color: active ? '#fff' : `${cat.bg}50`,
                              border: `1px solid ${active ? cat.bg : `${cat.bg}15`}`,
                              transform: active ? 'scale(1.12)' : 'scale(0.92)',
                              boxShadow: active ? `0 0 10px ${cat.bg}40` : 'none',
                              transition: 'all 0.5s ease',
                            }} />
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Exit funnel */}
          {simComplete && (
            <Box sx={{
              display: 'flex', justifyContent: 'center', mt: 0.5,
              animation: 'fadeIn 0.4s',
              '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
            }}>
              <Box sx={{
                width: 0, height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '10px solid #10b981',
                filter: 'drop-shadow(0 2px 4px rgba(16,185,129,0.4))',
              }} />
            </Box>
          )}

          {/* ── OUTPUT BUBBLE ── */}
          {simComplete && simScenario && (
            <Box sx={{
              mr: 'auto', maxWidth: '85%', mt: 0.75,
              px: 1.5, py: 0.75, borderRadius: '14px 14px 14px 4px',
              bgcolor: '#fff', border: '1px solid #e2e8f0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              animation: 'slideInLeft 0.5s ease-out',
              '@keyframes slideInLeft': { from: { opacity: 0, transform: 'translateX(-20px)' }, to: { opacity: 1, transform: 'translateX(0)' } },
            }}>
              <Typography sx={{ fontSize: 12, color: '#1e293b', fontWeight: 500, whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                {simScenario.response}
              </Typography>
              <Typography sx={{ fontSize: 9, color: '#6b7280', mt: 0.5 }}>
                NARA · ahora
              </Typography>
            </Box>
          )}

          </Box>
          {/* ═══ RIGHT: TERMINAL ═══ */}
          {isSimActive && simScenario && PIPELINE_LOGS[simScenario.message] && (
            <Box sx={{
              flex: { md: '1 1 48%' }, minWidth: 0, width: { xs: '100%' },
              mt: { xs: 1.5, md: 0 },
            }}>
            <Box ref={terminalRef} sx={{
              position: { md: 'sticky' }, top: '8px',
              bgcolor: '#0f172a', borderRadius: 2, overflow: 'auto',
              maxHeight: { xs: 200, md: 420 }, border: '1px solid #1e293b',
              boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.3)',
              fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#334155', borderRadius: 2 },
            }}>
              {/* macOS title bar */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                px: 1.25, py: 0.6, borderBottom: '1px solid #1e293b',
                bgcolor: '#1e293b', position: 'sticky', top: 0, zIndex: 1,
              }}>
                <Box sx={{ display: 'flex', gap: 0.4 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#ef4444' }} />
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#10b981' }} />
                </Box>
                <Typography sx={{ fontSize: 8, color: '#64748b', fontFamily: 'inherit', letterSpacing: 0.5 }}>
                  pm2 logs nara-backend --nostream
                </Typography>
              </Box>
              {/* Log lines */}
              <Box sx={{ px: 1.25, py: 0.75 }}>
                {(() => {
                  const logs = PIPELINE_LOGS[simScenario.message];
                  const visibleCount = simComplete ? logs.length : Math.min(simPhase + 1, logs.length);
                  return logs.slice(0, visibleCount).map((phaseLines, pi) => (
                    <React.Fragment key={pi}>
                      {phaseLines.map((line, li) => (
                        <Typography key={`${pi}-${li}`} sx={{
                          fontSize: 9.5, fontFamily: 'inherit', lineHeight: 1.55,
                          whiteSpace: 'pre', letterSpacing: 0.2,
                          color: line.startsWith('>')
                            ? '#38bdf8'
                            : line.includes('✓')
                              ? '#4ade80'
                              : /→.*(SALUDO|TOOL_USE|MEMORIA|INFORMACION|CONVERSACION|ESCALACION)/.test(line)
                                ? '#fbbf24'
                                : line.includes('⚡')
                                  ? '#f59e0b'
                                  : line.includes('~') && line.includes('ms')
                                    ? '#818cf8'
                                    : '#94a3b8',
                          animation: 'termLine 0.25s ease-out',
                          '@keyframes termLine': { from: { opacity: 0, transform: 'translateY(3px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                        }}>
                          {line}
                        </Typography>
                      ))}
                    </React.Fragment>
                  ));
                })()}
                {simulating && (
                  <Typography sx={{
                    fontSize: 10, fontFamily: 'inherit', color: '#10b981', mt: 0.25,
                    animation: 'blink 1s step-start infinite',
                    '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0 } },
                  }}>
                    ▊
                  </Typography>
                )}
              </Box>
            </Box>
            </Box>
          )}

        </Box>
        </Box>

        {/* Learning cycle - compact */}
        <Box sx={{
          mt: 2.5, maxWidth: 500, mx: 'auto',
          display: 'flex', gap: 1,
          opacity: simulating ? 0.15 : 1,
          transition: 'opacity 0.5s',
        }}>
          <Box sx={{
            flex: 1, p: 1.25, borderRadius: 2,
            bgcolor: 'rgba(225, 29, 72, 0.04)', border: '1px solid rgba(225, 29, 72, 0.12)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
              <PsychologyIcon sx={{ fontSize: 14, color: '#e11d48' }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#9f1239' }}>Correction Cache</Typography>
            </Box>
            <Typography sx={{ fontSize: 9, color: '#64748b', lineHeight: 1.3 }}>
              Reactivo — sugerencias confirmadas se inyectan al clasificador
            </Typography>
          </Box>
          <Box sx={{
            flex: 1, p: 1.25, borderRadius: 2,
            bgcolor: 'rgba(59, 130, 246, 0.04)', border: '1px solid rgba(59, 130, 246, 0.12)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
              <SchoolIcon sx={{ fontSize: 14, color: '#3b82f6' }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8' }}>Ejemplos Few-shot</Typography>
            </Box>
            <Typography sx={{ fontSize: 9, color: '#64748b', lineHeight: 1.3 }}>
              Proactivo — ejemplos pre-configurados por el admin
            </Typography>
          </Box>
        </Box>

      </Box>
      )}

      {/* Feature #4: Snackbar with Undo */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.undoId ? 5000 : 4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2, fontWeight: 600, alignItems: 'center' }}
          action={snackbar.undoId ? (
            <Button
              color="inherit"
              size="small"
              startIcon={<UndoIcon sx={{ fontSize: 16 }} />}
              onClick={() => handleUndo(snackbar.undoId)}
              sx={{ fontWeight: 700, fontSize: 12, textTransform: 'none' }}
            >
              Deshacer
            </Button>
          ) : snackbar.undoAction ? (
            <Button
              color="inherit"
              size="small"
              startIcon={<UndoIcon sx={{ fontSize: 16 }} />}
              onClick={() => { snackbar.undoAction(); setSnackbar({ ...snackbar, open: false }); }}
              sx={{ fontWeight: 700, fontSize: 12, textTransform: 'none' }}
            >
              Deshacer
            </Button>
          ) : undefined}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
